/**
 * QuotasSection Component
 * Daily quota usage display with progress bars and upgrade prompt
 */

import { useAuth } from '@contexts/AuthContext';
import { useGenerationStore } from '@stores/generationStore';
import { cn } from '@utils/cn';
import { type ReactElement, useEffect, useState } from 'react';

interface TrackingQuota {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
}

interface QuotasSectionProps {
  // Flags
  isFree: boolean;

  // Handlers
  onUpgradeClick: () => void;
}

export function QuotasSection({ isFree, onUpgradeClick }: QuotasSectionProps): ReactElement {
  const { authFetch } = useAuth();

  // Generation quota from store
  const quotaUsage = useGenerationStore((s) => s.quotaUsage);
  const fetchQuotaUsage = useGenerationStore((s) => s.fetchQuotaUsage);

  // Tracking quota fetched locally
  const [trackingQuota, setTrackingQuota] = useState<TrackingQuota | null>(null);

  useEffect(() => {
    fetchQuotaUsage(authFetch);
  }, [fetchQuotaUsage, authFetch]);

  useEffect(() => {
    authFetch('/api/parlays/tracking-quota')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: TrackingQuota | null) => {
        if (data) setTrackingQuota(data);
      })
      .catch(() => {});
  }, [authFetch]);

  return (
    <section data-testid="settings-quota-section" className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Daily Quotas</h3>
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
        {!quotaUsage ? (
          <div className="flex items-center gap-2 text-theme-text-secondary">
            <div className="w-4 h-4 border-2 border-theme-border border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading quotas...</span>
          </div>
        ) : quotaUsage.unavailable ? (
          <p className="text-sm text-theme-text-secondary text-center py-2">
            Daily quota usage is temporarily unavailable. Please refresh.
          </p>
        ) : (
          <>
            {/* Filter to only show modes with limit > 0 */}
            {quotaUsage.modes
              .filter((mode) => mode.limit > 0)
              .map((mode) => {
                const percentage = mode.limit > 0 ? (mode.used / mode.limit) * 100 : 0;
                const isLow = mode.remaining <= 2 && mode.remaining > 0;
                const isExhausted = mode.remaining === 0;

                return (
                  <div key={mode.mode} className="mb-4 last:mb-0">
                    {/* Mode name + counts */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-theme-text capitalize">
                          {mode.mode} Picks
                        </span>
                        {isExhausted && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-theme-danger/15 text-theme-danger">
                            Exhausted
                          </span>
                        )}
                        {isLow && !isExhausted && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-theme-warning/15 text-theme-warning">
                            Low
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-theme-text-secondary">
                        <span className="text-theme-text font-medium">{mode.used}</span> /{' '}
                        {mode.limit}{' '}
                        <span className="text-theme-text-tertiary">({mode.remaining} left)</span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                      <div
                        className={cn(
                          'h-full transition-all rounded-full',
                          isExhausted
                            ? 'bg-theme-danger'
                            : isLow
                              ? 'bg-theme-warning'
                              : 'bg-theme-primary',
                        )}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}

            {/* No quotas message (shouldn't happen, but handle edge case) */}
            {quotaUsage.modes.every((m) => m.limit === 0) && (
              <p className="text-sm text-theme-text-secondary text-center py-2">
                No pick generation quotas available for your tier.
              </p>
            )}

            {/* Tracking quota */}
            {trackingQuota && trackingQuota.limit > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-theme-text">Tracked Parlays</span>
                    {trackingQuota.remaining === 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-theme-danger/15 text-theme-danger">
                        Exhausted
                      </span>
                    )}
                    {trackingQuota.remaining > 0 && trackingQuota.remaining <= 5 && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-theme-warning/15 text-theme-warning">
                        Low
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-theme-text-secondary">
                    <span className="text-theme-text font-medium">{trackingQuota.used}</span> /{' '}
                    {trackingQuota.limit}{' '}
                    <span className="text-theme-text-tertiary">
                      ({trackingQuota.remaining} left)
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all rounded-full',
                      trackingQuota.remaining === 0
                        ? 'bg-theme-danger'
                        : trackingQuota.remaining <= 5
                          ? 'bg-theme-warning'
                          : 'bg-theme-primary',
                    )}
                    style={{
                      width: `${Math.min((trackingQuota.used / trackingQuota.limit) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Reset time */}
            <div className="mt-4 pt-4 border-t border-theme-border">
              <div className="flex items-center justify-between text-xs text-theme-text-tertiary">
                <span>Resets daily at:</span>
                <span className="text-theme-text-secondary font-medium">
                  {new Date(quotaUsage.resetsAt).toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZoneName: 'short',
                  })}
                </span>
              </div>
            </div>

            {/* Upgrade prompt for free users */}
            {isFree && (
              <div className="mt-4 pt-4 border-t border-theme-border">
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="w-full px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-sm font-medium rounded-lg transition-colors"
                >
                  Upgrade for More Picks
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
